import type { ShopperCustomersTypes } from "commerce-sdk-isomorphic";
import {
  type ContextType,
  type ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import toast, { type Toast } from "react-hot-toast";
import { useFetcher } from "react-router";
import { z } from "zod";

import { isAuthenticatedUser } from "~/.server/sessions.server";
import { validationError } from "~/components/forms/validationErrorResponse.server";

import { HeartFilledIcon, HeartIcon } from "@radix-ui/react-icons";
import { withZod } from "@rvf/zod";

import ToastNeutral from "~/components/toasts/toast-neutral";
import { useURL } from "~/contexts";
import {
  ADDED_TO_WISHLIST,
  REMOVED_FROM_WISHLIST,
  WISHLIST_LIMIT_REACHED,
} from "~/lib/constants";
import { ClientOnly, cn } from "~/lib/ui";

import { guestWishlistUtils } from "./utils/guest-wishlist-utils";

const RESOURCE_URL = "/resources/wishlist";
export const GUEST_WISHLIST_KEY = "guest_wishlist";
export const WISHLIST_ITEMS_THRESHOLD = 24;
export { RESOURCE_URL as WISHLIST_RESOURCE_URL };

export interface GuestWishlist {
  items: ShopperCustomersTypes.CustomerProductListItem[];
}

interface WishlistContext {
  wishlist?: ShopperCustomersTypes.CustomerProductList;
  addToList: (productId: string) => void;
  removeFromList: (productId: string) => void;
  isInList: (productId: string) => boolean;
  isLoggedIn: boolean;
}

const WishlistContext = createContext<WishlistContext | undefined>(undefined);

export const useWishlist = (): ContextType<typeof WishlistContext> => {
  return useContext(WishlistContext);
};

const WishlistSchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("add"),
    productId: z.string().min(2),
    color: z.string().optional(),
    size: z.string().optional(),
  }),
  z.object({
    action: z.literal("remove"),
    productId: z.string().min(2),
  }),
]);
type Actions = z.infer<typeof WishlistSchema>;

export const WishlistProvider = memo(
  ({ children }: { children: React.ReactNode }) => {
    const url = useURL();
    const fetcher = useFetcher<typeof loader>({ key: "wishlist" });
    useEffect(() => {
      fetcher.load(url(RESOURCE_URL));
    }, []);

    const [data, setWishlistData] = useState<{
      wishlist: ShopperCustomersTypes.CustomerProductList | undefined;
      isLoggedIn: boolean;
    }>({
      wishlist: [],
      isLoggedIn: false,
    });

    useEffect(() => {
      if (fetcher.data) {
        setWishlistData({
          wishlist: fetcher.data.wishlist,
          isLoggedIn: fetcher.data.isLoggedIn,
        });
      }
    }, [fetcher.data]);

    const isInList = (productId: string): boolean => {
      const isLoggedIn = fetcher.data?.isLoggedIn;

      if (!isLoggedIn) {
        return guestWishlistUtils.isInWishlist(productId);
      }

      if (
        !data?.wishlist?.customerProductListItems ||
        data?.wishlist?.customerProductListItems?.length < 1
      )
        return false;

      return data?.wishlist?.customerProductListItems?.some(
        product => product.productId === productId,
      );
    };

    return (
      <WishlistContext.Provider
        value={{
          ...data,
          isInList,
          addToList: (productId: string) => {
            setWishlistData(prev => {
              const newState = {
                ...prev,
                wishlist: {
                  ...prev.wishlist,
                  customerProductListItems:
                    prev.wishlist?.customerProductListItems ?? [],
                },
              };
              newState.wishlist?.customerProductListItems?.push({
                id: productId,
                productId,
              } as ShopperCustomersTypes.CustomerProductListItem);
              return newState;
            });
          },
          removeFromList: (productId: string) => {
            setWishlistData(prev => {
              const newState = {
                ...prev,
                wishlist: {
                  ...prev.wishlist,
                  customerProductListItems:
                    prev.wishlist?.customerProductListItems?.filter(
                      item => item.productId !== productId,
                    ) ?? [],
                },
              };
              return newState;
            });
          },
        }}
      >
        {children}
      </WishlistContext.Provider>
    );
  },
);
WishlistProvider.displayName = "WishlistProvider";

export const WishlistSchemaValidator = withZod(WishlistSchema);

export const loader = async ({ context }: LoaderArgs) => {
  const { shop } = context;
  const isLoggedIn = isAuthenticatedUser();
  let wishlist;
  if (isLoggedIn) wishlist = await shop.getWishlist();

  return { wishlist, isLoggedIn };
};

export const action = async ({ request, context: { shop } }: ActionArgs) => {
  const validation = await WishlistSchemaValidator.validate(
    await request.clone().formData(),
  );

  /* Handle any validation errros here */
  if (validation.error)
    return validationError(validation.error, validation.submittedData);

  const data = validation.data;
  try {
    switch (data.action) {
      case "add": {
        try {
          const response = await shop.addItemToWishlist(data.productId);

          return {
            successfullyAdded: true,
            wishlist: response,
          };
        } catch (error) {
          console.log("There was an error fetching the wishlist! ", error);

          return {
            successfullyAdded: false,
            wishlist: [],
          };
        }
      }
      case "remove": {
        try {
          await shop.removeItemFromWishlist(data.productId);

          return {
            successfullyRemoved: true,
          };
        } catch (error) {
          console.log(
            "There was an error removing item from the wishlist! ",
            error,
          );

          return {
            successfullyRemoved: false,
            wishlist: [],
          };
        }
      }
    }
  } catch (e) {
    console.error(e);
    return validationError((e as Error).message, validation.submittedData);
  }
};

export const useWishlistActions = () => {
  const fetcher = useFetcher<typeof action>();
  const url = useURL();
  const add = useCallback(
    (productId: string) => {
      fetcher.submit(
        {
          action: "add",
          productId,
        } satisfies Actions,
        { action: url(RESOURCE_URL), method: "post" },
      );
    },
    [fetcher],
  );

  const remove = useCallback(
    (productId: string) => {
      fetcher.submit(
        {
          action: "remove",
          productId,
        } satisfies Actions,
        { action: url(RESOURCE_URL), method: "post" },
      );
    },
    [fetcher],
  );
  return { add, remove, fetcher };
};

interface WishlistProps {
  productId: string;
  className?: string;
  iconWidth?: number;
  iconHeight?: number;
  children?: ReactNode;
  isWishlistPage?: boolean;
  isPDP?: boolean;
}
export const WishlistCTA = (props: WishlistProps) => {
  return <ClientOnly>{() => <WishlistCTAClient {...props} />}</ClientOnly>;
};

export const WishlistCTAClient = ({
  productId,
  className,
  children,
  iconWidth,
  iconHeight,
  isWishlistPage = false,
  isPDP = false,
}: WishlistProps) => {
  const wishlistUtils = useWishlist();
  const { add, remove, fetcher } = useWishlistActions();
  const [, setIsInGuestList] = useState<boolean>(false);

  const handleAddToWishlist = () => {
    if (!wishlistUtils) return;
    const { isLoggedIn, isInList, addToList, removeFromList, wishlist } =
      wishlistUtils;
    if (!isLoggedIn) {
      if (!isInList(productId)) {
        if (
          guestWishlistUtils.getWishlist().items.length ===
          WISHLIST_ITEMS_THRESHOLD
        ) {
          toastMessage(WISHLIST_LIMIT_REACHED);
          return;
        }
        guestWishlistUtils.addToWishlist(productId);
        setIsInGuestList(true);
        toastMessage(ADDED_TO_WISHLIST);
        return;
      }
      guestWishlistUtils.removeFromWishlist(productId);
      setIsInGuestList(false);
      toastMessage(REMOVED_FROM_WISHLIST);
      return;
    }

    if (!wishlist) return;

    if (!isInList(productId)) {
      if (
        wishlist.customerProductListItems?.length === WISHLIST_ITEMS_THRESHOLD
      ) {
        toastMessage(WISHLIST_LIMIT_REACHED);
        return;
      }

      addToList(productId);
      add(productId);
      return;
    }
    removeFromList(productId);
    remove(productId);
    return;
  };

  const toastMessage = (message: string) => {
    return toast.custom(
      (t: Toast) => (
        <div className="mt-20 shadow-lg">
          <ToastNeutral
            t={t}
            message={message}
            isLoggedIn={wishlistUtils?.isLoggedIn ?? false}
          />
        </div>
      ),
      {
        position: "top-right",
        duration: 3000,
      },
    );
  };

  useEffect(() => {
    if (
      fetcher.data &&
      "successfullyRemoved" in fetcher.data &&
      fetcher.data.successfullyRemoved
    ) {
      toastMessage(REMOVED_FROM_WISHLIST);
    } else if (
      fetcher.data &&
      "successfullyAdded" in fetcher.data &&
      fetcher.data.successfullyAdded
    ) {
      toastMessage(ADDED_TO_WISHLIST);
    }
  }, [fetcher.data]);

  return (
    <button className={className} onClick={handleAddToWishlist}>
      <div className={cn("", isWishlistPage && "hidden")}>
        <div
          className={cn(
            "rounded-full border border-wishlistIcon p-1",
            isPDP && "p-2",
            wishlistUtils?.isInList(productId) && "bg-black",
          )}
        >
          {wishlistUtils?.isInList(productId) ? (
            <HeartFilledIcon
              key={productId}
              width={iconWidth}
              height={iconHeight}
              color="white"
            />
          ) : (
            <HeartIcon key={productId} width={iconWidth} height={iconHeight} />
          )}
        </div>
      </div>
      {children}
    </button>
  );
};
